import React, { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash, ArrowCounterClockwise } from '@phosphor-icons/react';

function DataSection({ title, items, fields, onAdd, onDeactivate, onActivate }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({});

  const handleAdd = async () => {
    const missing = fields.filter(f => f.required && !form[f.key]?.trim());
    if (missing.length > 0) {
      toast.error(`Champs requis : ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    const ok = await onAdd(form);
    if (ok) { setForm({}); setAdding(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-sm">{title}</h3>
        <Button variant="ghost" size="sm" onClick={() => setAdding(!adding)} className="text-xs text-blue-600 h-7 px-2" data-testid={`add-${title.toLowerCase()}-btn`}>
          <Plus size={14} className="mr-1" />{adding ? 'Annuler' : 'Ajouter'}
        </Button>
      </div>

      {adding && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm space-y-2">
          {fields.map(f => (
            <div key={f.key}>
              <Label className="text-xs text-zinc-500">{f.label}{f.required ? ' *' : ''}</Label>
              <Input
                value={form[f.key] || ''}
                onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={f.placeholder || ''}
                className="mt-0.5 rounded-sm h-8 text-sm"
                data-testid={`new-${f.key}-input`}
              />
            </div>
          ))}
          <Button size="sm" onClick={handleAdd} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-xs h-7" data-testid={`save-${title.toLowerCase()}-btn`}>
            Ajouter
          </Button>
        </div>
      )}

      <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-100">
        {items.length === 0 ? (
          <p className="p-3 text-sm text-zinc-400 text-center">Aucun élément</p>
        ) : (
          items.map(item => (
            <div key={item.id} className={`flex items-center justify-between p-2.5 sm:p-3 ${item.active === false ? 'bg-zinc-100 opacity-60' : ''}`} data-testid={`data-item-${item.id}`}>
              <div className="min-w-0 flex-1">
                <p className={`font-medium text-sm truncate ${item.active === false ? 'line-through' : ''}`}>
                  {item.display || item.name || `${item.brand} ${item.model || ''}`}
                </p>
                {item.sub && <p className="text-xs text-zinc-500 truncate">{item.sub}</p>}
              </div>
              {item.active === false ? (
                <Button variant="ghost" size="sm" onClick={() => onActivate(item.id)} className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 flex-shrink-0" title="Réactiver">
                  <ArrowCounterClockwise size={14} />
                </Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => onDeactivate(item.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 flex-shrink-0" title="Désactiver">
                  <Trash size={14} />
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function DataManagementDialog({ open, onOpenChange }) {
  const [resins, setResins] = useState([]);
  const [pigments, setPigments] = useState([]);
  const [models, setModels] = useState([]);
  const [treatments, setTreatments] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [r, p, m, t] = await Promise.all([
        axios.get(`${API}/resins?include_inactive=true`),
        axios.get(`${API}/pigments?include_inactive=true`),
        axios.get(`${API}/batch-models?include_inactive=true`),
        axios.get(`${API}/post-treatments?include_inactive=true`),
      ]);
      setResins(r.data);
      setPigments(p.data);
      setModels(m.data);
      setTreatments(t.data);
    } catch (e) { toast.error('Erreur chargement'); }
  }, []);

  useEffect(() => { if (open) fetchAll(); }, [open, fetchAll]);

  const handleAddResin = async (form) => {
    try {
      await axios.post(`${API}/resins`, { brand: form.brand, model: form.model, reference: form.reference || null });
      toast.success('Résine ajoutée');
      fetchAll();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); return false; }
  };

  const handleAddPigment = async (form) => {
    try {
      await axios.post(`${API}/pigments`, { name: form.name, color_code: form.color_code || null, reference: form.reference || null });
      toast.success('Pigment ajouté');
      fetchAll();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); return false; }
  };

  const handleAddModel = async (form) => {
    try {
      await axios.post(`${API}/batch-models`, { name: form.name, description: form.description || null });
      toast.success('Modèle ajouté');
      fetchAll();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); return false; }
  };

  const handleAddTreatment = async (form) => {
    try {
      await axios.post(`${API}/post-treatments`, { name: form.name });
      toast.success('Post-traitement ajouté');
      fetchAll();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); return false; }
  };

  const deactivate = async (endpoint, id) => {
    try {
      await axios.put(`${API}/${endpoint}/${id}/deactivate`);
      toast.success('Désactivé');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const activate = async (endpoint, id) => {
    try {
      await axios.put(`${API}/${endpoint}/${id}/activate`);
      toast.success('Réactivé');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const resinItems = resins.map(r => ({
    ...r,
    display: `${r.brand} — ${r.model}`,
    sub: [r.reference, r.characteristics].filter(Boolean).join(' | ') || null,
  }));

  const pigmentItems = pigments.map(p => ({
    ...p,
    display: p.name,
    sub: [p.reference, p.color_code].filter(Boolean).join(' | ') || null,
  }));

  const modelItems = models.map(m => ({
    ...m,
    display: m.name,
    sub: m.description || null,
  }));

  const treatmentItems = treatments.map(t => ({
    ...t,
    display: t.name,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-[95vw] sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="data-management-dialog">
        <DialogHeader>
          <DialogTitle>Gérer les données</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="models" className="w-full">
          <TabsList className="w-full grid grid-cols-4 h-9">
            <TabsTrigger value="models" className="text-xs" data-testid="tab-models">Modèles</TabsTrigger>
            <TabsTrigger value="resins" className="text-xs" data-testid="tab-resins">Résines</TabsTrigger>
            <TabsTrigger value="pigments" className="text-xs" data-testid="tab-pigments">Pigments</TabsTrigger>
            <TabsTrigger value="treatments" className="text-xs" data-testid="tab-treatments">Post-trait.</TabsTrigger>
          </TabsList>

          <TabsContent value="models" className="mt-4">
            <DataSection
              title="Modèles"
              items={modelItems}
              fields={[
                { key: 'name', label: 'Nom', required: true, placeholder: 'Ex: K3' },
                { key: 'description', label: 'Description', required: false },
              ]}
              onAdd={handleAddModel}
              onDeactivate={(id) => deactivate('batch-models', id)}
              onActivate={(id) => activate('batch-models', id)}
            />
          </TabsContent>

          <TabsContent value="resins" className="mt-4">
            <DataSection
              title="Résines"
              items={resinItems}
              fields={[
                { key: 'brand', label: 'Marque', required: true, placeholder: 'Ex: Formlabs' },
                { key: 'model', label: 'Nom', required: true, placeholder: 'Ex: Flexible 80A' },
                { key: 'reference', label: 'Référence', required: false, placeholder: 'Ex: RS-F2-FLGR-02' },
              ]}
              onAdd={handleAddResin}
              onDeactivate={(id) => deactivate('resins', id)}
              onActivate={(id) => activate('resins', id)}
            />
          </TabsContent>

          <TabsContent value="pigments" className="mt-4">
            <DataSection
              title="Pigments"
              items={pigmentItems}
              fields={[
                { key: 'name', label: 'Nom', required: true, placeholder: 'Ex: Rouge Carmin' },
                { key: 'reference', label: 'Référence', required: false },
                { key: 'color_code', label: 'Code couleur', required: false, placeholder: '#FFB6C1' },
              ]}
              onAdd={handleAddPigment}
              onDeactivate={(id) => deactivate('pigments', id)}
              onActivate={(id) => activate('pigments', id)}
            />
          </TabsContent>

          <TabsContent value="treatments" className="mt-4">
            <DataSection
              title="Post-traitements"
              items={treatmentItems}
              fields={[
                { key: 'name', label: 'Nom', required: true, placeholder: 'Ex: UV Curing' },
              ]}
              onAdd={handleAddTreatment}
              onDeactivate={(id) => deactivate('post-treatments', id)}
              onActivate={(id) => activate('post-treatments', id)}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
